import { useCallback, useEffect, useRef, useState } from 'react';
import {
  type Connection,
  type OnEdgeUpdateFunc,
  type OnNodesChange,
  type NodeDimensionChange,
  useReactFlow,
  useStoreApi,
  OnEdgesChange,
  type NodeSelectionChange,
  type EdgeSelectionChange,
} from 'reactflow';
import { useHotkeys } from 'react-hotkeys-hook';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { useWorkflowStore, useUserWorkflowRole } from '@/state/workflow.state';
import { FF_ALT_DRAG_TO_DUPLICATE, FF_REMOVE_FROM_GROUP_WITH_MODIFIER } from '@/consts/featureFlags';
import { NodeType } from '@/enums/node-type.enum';
import { categorizeNodesGroupStatus } from '@/components/Nodes/utils/nodeUtils';
import { useFlowView } from '../FlowContext';
import { getEdgeIdsIntersectingSelectionRect, type SelectionRect } from '../utils/edgeSelectionGeometry';
import { useGroupAttachementsHighlighting } from './useGroupNodeHighlighting';
import { useAllowOverlayPassthrough, useSelectionActions } from './useSelectionActions';
import { useUserPreferences } from './useUserPreferences';
import type { MouseEvent as ReactMouseEvent } from 'react';
import type { Edge, Node } from '@/types/node';
import type { NodeId } from 'web';

/** Same shape as DuplicateDragSession in flowTypes; local alias for this hook. */
type AltDuplicateDragSession = {
  originalToNewId: Map<NodeId, NodeId>;
  frozenPositions: Map<NodeId, { x: number; y: number }>;
};

/**
 * React Flow invokes drag handlers with the D3 `sourceEvent` (a native `MouseEvent`), not a React
 * synthetic event — see @reactflow/core `useDrag` (`onStart(event.sourceEvent, ...)`). Using
 * `.nativeEvent` then yields `undefined`, so modifier keys are never detected.
 */
function getNativePointerEvent(event: ReactMouseEvent | MouseEvent): MouseEvent {
  if (event && typeof event === 'object' && 'nativeEvent' in event && event.nativeEvent) {
    return event.nativeEvent;
  }
  return event as MouseEvent;
}

/** Option (macOS) and Alt (Windows/Linux) both set `altKey`; `getModifierState('Alt')` covers edge cases. */
function isAltOrOptionDuplicateModifier(event: MouseEvent): boolean {
  if (event.altKey) return true;
  if (typeof event.getModifierState === 'function') {
    return event.getModifierState('Alt');
  }
  return false;
}

const useGroupInteractionContext = () => {
  const [isModifierPressed, setIsModifierPressed] = useState(false);
  const isRemoveFromGroupWithModifierEnabled = useFeatureFlagEnabled(FF_REMOVE_FROM_GROUP_WITH_MODIFIER);

  useHotkeys(
    'meta,ctrl',
    (event) => {
      if (isRemoveFromGroupWithModifierEnabled) {
        setIsModifierPressed(event.metaKey || event.ctrlKey);
      }
    },
    {
      keydown: isRemoveFromGroupWithModifierEnabled,
      keyup: isRemoveFromGroupWithModifierEnabled,
    },
  );

  useEffect(() => {
    // Clear on focus loss (covers Cmd+Tab, window switches, etc.)
    const handleBlur = () => setIsModifierPressed(false);
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') setIsModifierPressed(false);
    };

    window.addEventListener('blur', handleBlur);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('blur', handleBlur);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [setIsModifierPressed]);

  return { isModifierPressed };
};

export const useReactFlowProps = () => {
  const flowView = useFlowView();
  const { screenToFlowPosition } = useReactFlow();
  const store = useStoreApi();
  const role = useUserWorkflowRole();
  const { userPrefEdgeType } = useUserPreferences();

  const lastPaneSelectionRectRef = useRef<SelectionRect | null>(null);
  const prevSelectionRef = useRef<NodeId[]>([]);

  const { replaceSelection } = useSelectionActions();
  useAllowOverlayPassthrough();

  useEffect(() => {
    return store.subscribe((state) => {
      const r = state.userSelectionRect;
      if (r && (r.width > 0 || r.height > 0)) {
        lastPaneSelectionRectRef.current = {
          x: r.x,
          y: r.y,
          width: r.width,
          height: r.height,
          startX: r.startX,
          startY: r.startY,
        };
      }
    });
  }, [store]);

  const { isModifierPressed } = useGroupInteractionContext();
  const isAltDragToDuplicateEnabled = useFeatureFlagEnabled(FF_ALT_DRAG_TO_DUPLICATE);
  const duplicateDragSessionRef = useRef<AltDuplicateDragSession | null>(null);
  const { setSuppressedSelectionNode, clearNodeIdToSuppressSelection, suppressedSelectionNode } = useWorkflowStore();

  const { highlightPotentialGroupAttachements, clearAttachementHighlights } = useGroupAttachementsHighlighting();

  const newConnectionRef = useRef<{
    source: string;
    target: string;
    sourceHandle: string;
    targetHandle: string;
  }>(undefined);
  const setInvalidConnection = useWorkflowStore((state) => state.setInvalidConnection);

  const isValidConnection = useCallback(
    ({ source, target, sourceHandle, targetHandle }: Connection) => {
      if (!source || !target) return false;
      if (flowView.wouldConnectionResultInCycle(source, target)) {
        setInvalidConnection({
          errorId: source === target ? 'self-connection' : 'cyclic',
          source,
          target,
          sourceHandle,
          targetHandle,
        });
        return false;
      }
      return true;
    },
    [flowView, setInvalidConnection],
  );

  const onEdgeUpdateStart = useCallback((_, _edge: Edge) => {
    // TODO ongoing remove
  }, []);
  const onEdgeUpdate: OnEdgeUpdateFunc = useCallback((_oldEdge: Edge, newConnection: Connection) => {
    const { source, target, sourceHandle, targetHandle } = newConnection;
    if (source !== null && target !== null && sourceHandle !== null && targetHandle !== null) {
      newConnectionRef.current = {
        source,
        target,
        sourceHandle,
        targetHandle,
      };
    }
  }, []);

  const onEdgeUpdateEnd = useCallback(
    (_e: MouseEvent, oldEdge: Edge) => {
      const newConnection = newConnectionRef.current;
      if (newConnection) {
        flowView.reconnectEdge(
          newConnection.source,
          newConnection.target,
          newConnection.sourceHandle,
          newConnection.targetHandle,
          oldEdge,
        );
      } else {
        flowView.deleteEdges([oldEdge.id]);
      }

      newConnectionRef.current = undefined;
    },
    [flowView],
  );

  const capturePrevSelection = useCallback(() => {
    prevSelectionRef.current = [...flowView.selectedNodes];
  }, [flowView]);

  const onNodesChange = useCallback(
    (changes: Parameters<OnNodesChange>[0]) => {
      const selectionChanges: NodeSelectionChange[] = changes.filter((c) => c.type === 'select');
      if (selectionChanges.length > 0) {
        const currentSelectedNodes = flowView.selectedNodes;

        const selectedNodeIds = new Set(currentSelectedNodes);
        for (const change of selectionChanges) {
          const { id, selected } = change;
          if (selected) {
            selectedNodeIds.add(id);
          } else {
            selectedNodeIds.delete(id);
          }
        }
        const nextSelectedNodes = [...selectedNodeIds];
        replaceSelection(nextSelectedNodes);
      }

      const dimensionChanges = changes.filter(
        (c): c is NodeDimensionChange => c.type === 'dimensions' && c.dimensions !== undefined,
      );

      if (dimensionChanges.length > 0) {
        flowView.setNodeDimensions(
          dimensionChanges.map((c) => ({
            nodeId: c.id,
            width: c.dimensions!.width,
            height: c.dimensions!.height,
          })),
        );
      }

      // Note: Other change types are not handled here
    },
    [flowView, replaceSelection],
  );

  const onEdgesChange = useCallback(
    (changes: Parameters<OnEdgesChange>[0]) => {
      const selectionChanges: EdgeSelectionChange[] = changes.filter((c) => c.type === 'select');
      if (selectionChanges.length === 0) return;

      // RF can emit multiple select changes per edge in one batch (e.g. true then false); last wins.
      const finalSelectedByEdgeId = new Map<string, boolean>();
      for (const { id, selected } of selectionChanges) {
        finalSelectedByEdgeId.set(id, selected);
      }
      const selectedEdges = [...flowView.explicitSelectedEdges];
      for (const [edgeId, selected] of finalSelectedByEdgeId) {
        const idx = selectedEdges.indexOf(edgeId);
        if (selected) {
          if (idx === -1) {
            selectedEdges.push(edgeId);
          }
        } else if (idx !== -1) {
          selectedEdges.splice(idx, 1);
        }
      }
      flowView.selectedEdges = selectedEdges;
    },
    [flowView],
  );

  const onConnect = useCallback(
    ({ source, target, sourceHandle, targetHandle }: Connection) => {
      if (!source || !target || !sourceHandle || !targetHandle) return;
      flowView.connectEdge(source, target, sourceHandle, targetHandle);
    },
    [flowView],
  );

  const resolveNodeIdsForAltDuplicate = useCallback(
    (primaryNodeId: NodeId) => {
      const sel = flowView.selectedNodes;
      if (sel.length > 1 && sel.includes(primaryNodeId)) {
        return [...sel];
      }
      return [primaryNodeId];
    },
    [flowView],
  );

  const tryBeginDuplicateDrag = useCallback(
    (event: MouseEvent, nodeIds: NodeId[]) => {
      if (!isAltDragToDuplicateEnabled || role === 'guest') return;
      if (!isAltOrOptionDuplicateModifier(event)) return;
      if (duplicateDragSessionRef.current) return;
      const session = flowView.beginDuplicateDrag(nodeIds, { preserveExternalInputs: true });
      duplicateDragSessionRef.current = session;
    },
    [flowView, role, isAltDragToDuplicateEnabled],
  );

  const onNodeDragStart = useCallback(
    (event: ReactMouseEvent | MouseEvent, node: Node) => {
      tryBeginDuplicateDrag(getNativePointerEvent(event), resolveNodeIdsForAltDuplicate(node.id));
    },
    [tryBeginDuplicateDrag, resolveNodeIdsForAltDuplicate],
  );

  const onSelectionDragStart = useCallback(
    (event: ReactMouseEvent | MouseEvent, nodes: Node[]) => {
      if (nodes.length === 0) return;
      tryBeginDuplicateDrag(
        getNativePointerEvent(event),
        nodes.map((n) => n.id),
      );
    },
    [tryBeginDuplicateDrag],
  );

  const onNodeDrag = useCallback(
    (_: unknown, node: Node) => {
      const duplicateSession = duplicateDragSessionRef.current;
      // Always update positions immediately (must be smooth)
      flowView.handleNodeDrag([node], isModifierPressed, true, duplicateSession);

      const skipAttachHighlight = isModifierPressed || duplicateSession !== null;
      // Throttled highlighting (can be delayed without affecting UX)
      // Only highlight for group nodes and ungrouped nodes (grouped nodes don't need highlighting)
      if (!skipAttachHighlight && (node.type === NodeType.CustomGroup || !node.parentId)) {
        const { groupNodes, ungroupedNodes } = categorizeNodesGroupStatus([node]);
        highlightPotentialGroupAttachements(groupNodes, ungroupedNodes);
      }
    },
    [flowView, isModifierPressed, highlightPotentialGroupAttachements],
  );

  const onNodeDragStop = useCallback(
    (_: unknown, node: Node) => {
      // Cancel any pending throttled highlights
      highlightPotentialGroupAttachements.cancel();

      const duplicateSession = duplicateDragSessionRef.current;
      flowView.handleNodeDrag([node], isModifierPressed, false, duplicateSession);
      duplicateDragSessionRef.current = null;

      // Clear highlights after drag stop
      clearAttachementHighlights();
    },
    [flowView, isModifierPressed, clearAttachementHighlights, highlightPotentialGroupAttachements],
  );

  const onSelectionDrag = useCallback(
    (_: unknown, nodes: Node[]) => {
      const duplicateSession = duplicateDragSessionRef.current;
      // Always update positions immediately (must be smooth)
      flowView.handleNodeDrag(nodes, isModifierPressed, true, duplicateSession);

      const skipAttachHighlight = isModifierPressed || duplicateSession !== null;
      // Throttled highlighting (can be delayed without affecting UX)
      // Filter to only nodes that need highlighting (groups and ungrouped nodes)
      if (!skipAttachHighlight) {
        const nodesToHighlight = nodes.filter((node) => node.type === NodeType.CustomGroup || !node.parentId);
        if (nodesToHighlight.length > 0) {
          const { groupNodes, ungroupedNodes } = categorizeNodesGroupStatus(nodesToHighlight);
          highlightPotentialGroupAttachements(groupNodes, ungroupedNodes);
        }
      }
    },
    [flowView, isModifierPressed, highlightPotentialGroupAttachements],
  );

  const onSelectionDragStop = useCallback(
    (_: unknown, nodes: Node[]) => {
      // Cancel any pending throttled highlights
      highlightPotentialGroupAttachements.cancel();

      const duplicateSession = duplicateDragSessionRef.current;
      flowView.handleNodeDrag(nodes, isModifierPressed, false, duplicateSession);
      duplicateDragSessionRef.current = null;

      // Clear highlights after drag stop
      clearAttachementHighlights();
    },
    [flowView, isModifierPressed, clearAttachementHighlights, highlightPotentialGroupAttachements],
  );

  const CLICK_THRESHOLD = 5; // pixels - treat as click if movement less than this

  /**
   * These selection handlers support multi-selecting group children using drag.
   * They both trigger selection if drag distance is < CLICK_THRESHOLD and also prevent the group itself from
   * being selected while children multiselection is in progress.
   */
  const onSelectionStart = useCallback(
    (event: MouseEvent) => {
      lastPaneSelectionRectRef.current = null;

      const flowPosition = screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

      const nodeAtPosition = flowView.findNodeAtPosition(flowPosition);

      if (nodeAtPosition && nodeAtPosition.type === NodeType.CustomGroup) {
        const isAlreadySelected = flowView.isNodeSelected(nodeAtPosition.id);
        if (!isAlreadySelected) {
          setSuppressedSelectionNode(nodeAtPosition.id, event.clientX, event.clientY);
        }
      }
    },
    [setSuppressedSelectionNode, flowView, screenToFlowPosition],
  );

  /**
   * This handler also supports multi-selecting group children using drag.
   * It checks if the selection was a click on an unselected group node and if so, selects the group node.
   * It also clears the suppressed selection node.
   */
  const onSelectionEnd = useCallback(
    (event: MouseEvent) => {
      // Check if this was a click (not drag) on an unselected group node
      if (suppressedSelectionNode) {
        const dx = event.clientX - suppressedSelectionNode.startX;
        const dy = event.clientY - suppressedSelectionNode.startY;
        const distance = Math.sqrt(dx * dx + dy * dy);

        // If movement was minimal, select the group node
        if (distance < CLICK_THRESHOLD) {
          const nextSelectedNodes = event.shiftKey
            ? [...new Set([...prevSelectionRef.current, suppressedSelectionNode.nodeId])]
            : [suppressedSelectionNode.nodeId];
          replaceSelection(nextSelectedNodes);
        }
      }

      // React Flow only selects edges incident to nodes inside the box; add edges whose path intersects the box.
      if (role !== 'guest') {
        const paneRect = lastPaneSelectionRectRef.current;
        if (paneRect && paneRect.width >= 4 && paneRect.height >= 4) {
          const s = store.getState();
          const intersectingIds = getEdgeIdsIntersectingSelectionRect({
            paneRect,
            transform: s.transform,
            edges: s.edges,
            nodeInternals: s.nodeInternals,
            connectionMode: s.connectionMode,
            edgeType: userPrefEdgeType,
          });
          if (intersectingIds.length > 0) {
            const current = flowView.explicitSelectedEdges;
            flowView.selectedEdges = [...new Set([...current, ...intersectingIds])];
          }
        }
      }

      clearNodeIdToSuppressSelection();
      prevSelectionRef.current = [];
    },
    [
      clearNodeIdToSuppressSelection,
      flowView,
      replaceSelection,
      suppressedSelectionNode,
      role,
      store,
      userPrefEdgeType,
    ],
  );

  /**
   * React Flow's `addSelectedEdges` uses `getSelectionChanges(edges, [clickedId])`, which does **not** emit
   * `{ selected: true }` for an edge that already has `selected: true` on the props. Incident edges get
   * `selected: true` from our merge (incident ∪ explicit), so `onEdgesChange` only receives deselects for
   * other edges and our explicit list becomes `[]`. RF calls this after the internal handler — set explicit
   * selection to the clicked edge for non-multi clicks.
   */
  const onEdgeClick = useCallback(
    (event: ReactMouseEvent, edge: { id: string }) => {
      if (role === 'guest') return;
      if (event.shiftKey || event.metaKey || event.ctrlKey) return;
      flowView.selectedEdges = [edge.id];
    },
    [flowView, role],
  );

  return {
    capturePrevSelection,
    onConnect,
    onEdgeUpdateStart,
    onEdgeUpdate,
    onEdgeUpdateEnd,
    onEdgeClick,
    onEdgesChange,
    onNodesChange,
    onNodeDragStart,
    onNodeDrag,
    onNodeDragStop,
    onSelectionDragStart,
    onSelectionDrag,
    onSelectionDragStop,
    onSelectionStart,
    onSelectionEnd,
    isValidConnection,
  };
};
